import React from 'react';

import { Link } from 'react-router-dom';
import { Toggle } from 'components/inputs';
import { ProfilePicture, DateDisplay } from 'components/ui';

import { isBC, isAdmin } from 'functions/login';
import { DEFAULT_PROFILE } from 'components/constants';
import { dateFilter, yesNoFilter, yesNoFilterRender } from 'functions/tables';

// A soldier with no platoon cannot be opened -- the profile endpoint cannot
// serialize a missing platoon, so the request fails. Flag those rows instead
// of linking to a page that will not load.
export const isIncomplete = user => !user.class_id || !user.platoon;

export const INCOMPLETE_REASON =
  'Not assigned to a platoon. Assign this soldier to a platoon to open their profile.';

// name / serial / rank cells: link normally, plain text when info is missing
const soldierCell = ( linkProps = {} ) => ({ original, value }) =>
  isIncomplete( original )
    ? <span title={ INCOMPLETE_REASON }>{ value }</span>
    : <Link to={`/bm/soldiers/${original.user_id}`} { ...linkProps }>{ value }</Link>;

export default ( login, editPicture, updateToggle ) => {
  // define the table for the page
  let columns = [
    {
      Header: 'Profile',  accessor: 'profilePicture',
      className: 'profile-picture', width: 85, sortable: false,
      Cell: props => <ProfilePicture src={ props.value } className='inline-profile' 
                        onClick={ editPicture( props.original.user_id ) }/>,
      Filter: ({ filter, onChange }) =>
        <select style={{ width: "100%" }} value={filter ? filter.value : "all"}
          onChange={event => onChange(event.target.value)}>
          <option value="all">Show All</option>
          <option value="yes">Has Profile</option>
          <option value="no">No Profile</option>
        </select>,
      filterMethod: ( filter, row ) => {
        if ( filter.value === 'all' ) return true;
        if ( filter.value === 'yes') return row[filter.id] !== DEFAULT_PROFILE;
        if ( filter.value === 'no') return row[filter.id] === DEFAULT_PROFILE;
      },
    }, {
      Header: "First Name", accessor: 'first',
      Cell: soldierCell({ tabIndex: -1 }),
    }, {
      Header: "Last Name", accessor: 'last',
      Cell: soldierCell({ tabIndex: -1 }),
    }, {
      Header: "Serial Number", accessor: 'user_serial',
      Cell: soldierCell(),
    }, {
      Header: 'Rank', accessor: 'rank_name',
      Cell: soldierCell(),
    }, {
      Header: 'Date Of Birth', accessor: 'dob',
      Cell: props => <DateDisplay value={ props.value } format = 'l'/>,
      filterMethod: dateFilter,
    }, {
      Header: 'Family ID', accessor: 'admin_id',
      Cell: props => props.value
    }
  ];

  // * Chayolei columns
  if ( login.modules.chayolei ) { columns.push(
    {
      Header: 'Registered', accessor: 'user_registered',
      Cell: props => <DateDisplay value={ props.value } format = 'l LT'/>,
      filterMethod: ( filter, row ) => {
        if ( filter.value === 'all' ) return true;
        if ( filter.value === 'yes') return !!row[filter.id];
        if ( filter.value === 'no') return !row[filter.id];
      },
      Filter: ({ filter, onChange }) =>
        <select style={{ width: "100%" }} value={filter ? filter.value : "all"}
          onChange={event => onChange(event.target.value)}>
          <option value="all">Show All</option>
          <option value="yes">Registered</option>
          <option value="no">Not Registered</option>
        </select>
    },
    { Header: 'CTH', accessor: 'chayolei',
      Cell: ({ value, original }) => <Toggle checked={ !!value } 
        onChange={ updateToggle( 'chayolei', original.user_id ) } />,
      Filter: yesNoFilterRender(), filterMethod: yesNoFilter
    }
  )};

  // * Chidon columns
  if ( login.modules.chidon ) {
    columns.push(
      { Header: 'Chidon', accessor: 'chidon',
        Cell: ({ value, original }) => <Toggle checked={ !!value } 
          onChange={ updateToggle( 'chidon', original.user_id ) } />,
        Filter: yesNoFilterRender(), filterMethod: yesNoFilter
      }
    );
  }

  // * Tanya columns
  if ( login.modules.tanya ) {
    columns.push(
      { Header: 'Tanya', accessor: 'yan',
        Cell: ({ value, original }) => <Toggle checked={ !!value } 
          onChange={ updateToggle( 'yan', original.user_id ) } />,
        Filter: yesNoFilterRender(), filterMethod: yesNoFilter
      }
    );
  }
  
  // * Lulav columns
  columns.push(
    { Header: 'Lulav', accessor: 'lulav',
      Cell: ({ value, original }) => <Toggle checked={ !!value } 
        onChange={ updateToggle( 'lulav', original.user_id ) } />,
      Filter: yesNoFilterRender(), filterMethod: yesNoFilter
    }
  );

  // * Base Commander columns
  if ( isBC( login.code ) ) {
    columns.push({
      Header: "Platoon", id: 'platoon', accessor: user => user.platoon ? user.platoon.name : '-',
      // without a class_id the link resolves to /bm/platoons/false, so show plain text
      Cell: props => props.original.platoon
        ? <Link to={`/bm/platoons/${props.original.class_id}`} tabIndex={ -1 }>{props.value}</Link>
        : <span title={ INCOMPLETE_REASON }>{ props.value }</span>,
    });
  }

  // * Institution columns
  if ( isAdmin( login.code ) ) {
    columns.push({
      Header: "Base", id: 'base', accessor: user => user.school.school_name,
      Cell: props => <Link to={`/bm/base/${props.original.school_id}`} tabIndex={ -1 }>{props.value}</Link>,
    });
  }

  return columns;
}



// WEBPACK FOOTER //
// ./src/pages/base-managment/soldiers/SoldiersPage/columns.js